import { message } from 'antd';
import { getIntl } from 'umi';

import { hydrateCollaboratorUsers } from '@/utils/collaborators';
import { CoreEnums } from '@/utils/domain/constants';
import { toPaginate } from '@/utils/infrastructure';
import { VALIDATION_INTERNAL_ERRORS } from '@/utils/infrastructure/constants';
import CoreQueries from '@/utils/input/queries';
import {
  getAIAgentById,
  updateAIAgent,
  createAIAgent,
  deleteAIAgent,
  generateAIAgentConfigToken,
  getAIAgentVersionConfig,
  appendAIAgentConfigElement,
  modifyAIAgentConfigElement,
  removeAIAgentConfigElement,
  publishAIAgentVersion,
  getAIAgentVersions,
  getAIAgentConfigTraces,
  getAIAgentConfigDependencies,
  getAIAgentCollaborators,
  addAIAgentCollaborator,
  removeAIAgentCollaborator,
} from '../service';

// Per-tab cache key for the builder token (matches list-builder convention).
// The window.tabID is set by the app bootstrap so multiple tabs don't share
// the same in-flight version draft / token.
const TOKEN_STORAGE_KEY = `x-${window.tabID}-agent-token`;

// Backend serialises AccessMode as the kebab-case enum NAME ('write',
// 'readonly') in the AIAgentConfigurationToken envelope and accepts the same
// strings in URL path segments. Some legacy callers still send the numeric
// enum, so we accept both shapes when checking for write capability.
const AGENT_ACCESS_MODE = Object.freeze({ Write: 'write', ReadOnly: 'readonly' });
const isWriteMode = (mode) =>
  mode === AGENT_ACCESS_MODE.Write || mode === CoreEnums.AccessMode.Write;

const writeCachedToken = (tokenData) => {
  if (!tokenData) return;
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
  } catch {
    // localStorage may be unavailable (private mode / quota exceeded).
  }
};

const clearCachedToken = () => {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // Silent fail.
  }
};

const paginationMeta = { page: 1, limit: 20, count: 0, totalPages: 0 };

// Extract { aiAgentId, versionId, token, mode } from the cached token in state.
// All builder mutation effects rely on this trio, so centralising the lookup
// keeps the effects readable and avoids subtle desync bugs.
function* getTokenContext(select) {
  const tk = yield select((s) => s.agentBuilder.generatedToken || {});
  return {
    aiAgentId: tk.aiAgentId,
    versionId: tk.versionId,
    token: tk.token,
    mode: tk.mode,
  };
}

const initialState = {
  // Base entity (AIAgent metadata + mirror fields). Loaded via getAIAgentById
  // with the ManageAgents.edit OData query so collaborators/skills/connections
  // come back with full {id, name} shape (not raw GUIDs).
  currentAgent: null,

  // In-memory partial form values pending a manual save. Panels push slices
  // here; the PUT /ai/agent is issued only when the user clicks "Guardar"
  // in the header (legacy UX flow that the current UI still relies on).
  draft: {},

  // Versioned configuration state (token-scoped) — mirrors listBuilder.
  generatedToken: null, // { aiAgentId, versionId, versionNumber, mode, token }
  versions: [],
  configurations: null, // AIAgentVersion.config snapshot (greetings, guardrails, ...)
  dependencies: [],
  traces: [],
  tracesPagination: paginationMeta,
  tracesOrderBy: 'created desc',

  // Filters config registry consumed by the shared `<FiltersDrawer />` —
  // the panel that owns the active filters (Traces) pushes its config
  // here so the drawer knows which inputs to render. Mirrors the same
  // pattern used by `listBuilder` / `entity` builders.
  filtersConfig: [],

  // Collaborators slice — backs the shared <CollaboratorsDrawer /> in the
  // agent-builder. Mirrors the entity / list / app builders.
  collaborators: [],

  // Publish checkpoint — populated when the backend generated the internal
  // summary but flagged warnings (`AIAgentPublishResult.published === false`
  // with `inconsistencies`). Drives the "review warnings" modal. The user can
  // cancel (keep editing) or force-publish. Shape: { inconsistencies, result }.
  publishInconsistencies: null,

  // UI flags.
  loading: false,
  saving: false,
  publishing: false,
  initError: null,
};

const Model = {
  namespace: 'agentBuilder',
  state: initialState,

  effects: {
    // ─────────────────────────────────────────────────────────────────────
    // Bootstrap & lifecycle
    // ─────────────────────────────────────────────────────────────────────

    *initBuilder({ payload }, { call, put }) {
      const { agentId } = payload || {};
      if (!agentId) {
        yield put({ type: 'initFailed', payload: 'agent' });
        return;
      }

      yield put({ type: 'setLoading', payload: true });

      let agent;
      try {
        const response = yield call(getAIAgentById, agentId, CoreQueries.ManageAgents.edit);
        agent = response?.data || response;
        if (!agent || !agent.id) throw new Error('not-found');
        yield put({ type: 'saveAgent', payload: agent });
      } catch (e) {
        yield put({ type: 'setLoading', payload: false });
        yield put({ type: 'initFailed', payload: 'agent' });
        return;
      }

      // Load versions list (non-critical — UI degrades gracefully without it).
      try {
        const versionsRes = yield call(getAIAgentVersions, agentId);
        const items = versionsRes?.items || versionsRes?.data?.items || [];
        yield put({ type: 'saveVersions', payload: items });
      } catch (e) {
        // Non-critical: keep versions empty.
      }

      // Generate a write token for the latest draft (backend creates a new
      // draft if the latest version is already published).
      try {
        const tokenRes = yield call(generateAIAgentConfigToken, agentId, AGENT_ACCESS_MODE.Write);
        const token = tokenRes?.data || tokenRes;
        if (!token?.token) throw new Error('token-not-issued');

        writeCachedToken(token);
        yield put({ type: 'saveToken', payload: token });

        if (token.aiAgentId && token.versionId) {
          yield put({
            type: 'fetchAgentConfig',
            payload: {
              aiAgentId: token.aiAgentId,
              versionId: token.versionId,
              token: token.token,
            },
          });
        }
      } catch (e) {
        // Token generation failures are non-blocking for the legacy UI: the
        // agent entity is still usable for metadata edits via PUT /ai/agent.
        // We surface initError='token' so a future UI can show a banner.
        yield put({ type: 'initFailed', payload: 'token' });
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },

    *refreshAgent(_action, { call, put, select }) {
      try {
        const agentId = yield select((s) => s.agentBuilder.currentAgent?.id);
        if (!agentId) return;
        const response = yield call(getAIAgentById, agentId, CoreQueries.ManageAgents.edit);
        const agent = response?.data || response;
        if (agent && agent.id) {
          yield put({ type: 'saveAgent', payload: agent });
        }
      } catch (e) {
        // Silent — global error handler already surfaces it.
      }
    },

    // ─────────────────────────────────────────────────────────────────────
    // Versions & tokens
    // ─────────────────────────────────────────────────────────────────────

    *fetchVersions({ payload }, { call, put, select }) {
      try {
        const agentId =
          payload?.aiAgentId || (yield select((s) => s.agentBuilder.currentAgent?.id));
        if (!agentId) return;
        const res = yield call(getAIAgentVersions, agentId);
        const items = res?.items || res?.data?.items || [];
        yield put({ type: 'saveVersions', payload: items });
      } catch (e) {
        // Silent — non-critical.
      }
    },

    *changeVersion({ payload }, { call, put, select }) {
      const { versionId } = payload || {};
      if (!versionId) return;

      const agentId = yield select((s) => s.agentBuilder.currentAgent?.id);
      const versions = yield select((s) => s.agentBuilder.versions);
      if (!agentId) return;

      const selected = versions.find((v) => v.id === versionId);
      const mode = selected?.published ? AGENT_ACCESS_MODE.ReadOnly : AGENT_ACCESS_MODE.Write;

      yield put({ type: 'setLoading', payload: true });

      try {
        const tokenRes = yield call(generateAIAgentConfigToken, agentId, mode, versionId);
        const token = tokenRes?.data || tokenRes;
        if (!token?.token) throw new Error('token-not-issued');

        writeCachedToken(token);
        yield put({ type: 'saveToken', payload: token });

        if (token.aiAgentId && token.versionId) {
          yield put({
            type: 'fetchAgentConfig',
            payload: {
              aiAgentId: token.aiAgentId,
              versionId: token.versionId,
              token: token.token,
            },
          });
        }
      } catch (e) {
        // Handled globally.
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },

    *fetchAgentConfig({ payload }, { call, put, select }) {
      try {
        const token = payload?.token || (yield select((s) => s.agentBuilder.generatedToken?.token));
        const agentId =
          payload?.aiAgentId || (yield select((s) => s.agentBuilder.generatedToken?.aiAgentId));
        const versionId =
          payload?.versionId || (yield select((s) => s.agentBuilder.generatedToken?.versionId));
        if (!token || !agentId || !versionId) {
          yield put({ type: 'saveConfigurations', payload: null });
          return;
        }

        const res = yield call(getAIAgentVersionConfig, agentId, versionId, token);
        // Persist the FULL AIAgentVersion (root fields + nested configurations)
        // because several pieces of state live at the root of the version
        // envelope — not inside `configurations.config`:
        //   - instructions  ← AIAgentVersion.instructions
        //   - name / email / provider / modelId / memoryId / debugModeEnabled
        //   - number / current / published / publishedDate / publisher
        // mapAgentToFormValues (in utils.js) and the AgentCard tolerate both
        // shapes (full envelope OR flat config) so downstream consumers stay
        // unchanged; this just stops us from losing the version-root data.
        const versionEnvelope = res?.data || res || null;
        yield put({ type: 'saveConfigurations', payload: versionEnvelope });
      } catch (e) {
        yield put({ type: 'saveConfigurations', payload: null });
      }
    },

    // ─────────────────────────────────────────────────────────────────────
    // Traces & dependencies
    // ─────────────────────────────────────────────────────────────────────

    *fetchTraces({ payload }, { call, put, select }) {
      try {
        const agentId = payload?.id || (yield select((s) => s.agentBuilder.currentAgent?.id));
        if (!agentId) return;
        const { versionId, token } = yield select((s) => s.agentBuilder.generatedToken || {});
        const vid = payload?.versionId || versionId;
        const res = yield call(getAIAgentConfigTraces, agentId, vid, {
          token,
          limit: payload?.limit || 20,
          page: payload?.page || 1,
          ...(payload?.orderBy && { orderBy: payload.orderBy }),
          ...(payload?.search && {
            search: payload.search,
            searchSelect: payload.searchSelect,
          }),
          ...(payload?.filter && { filter: payload.filter }),
        });
        const data = res?.data || res;
        yield put({ type: 'queryTraces', payload: { ...data, ...payload } });
      } catch (e) {
        yield put({ type: 'queryTraces', payload: { items: [], page: 1, limit: 20 } });
      }
    },

    *fetchDependencies({ payload }, { call, put, select }) {
      try {
        const agentId =
          payload?.aiAgentId || (yield select((s) => s.agentBuilder.currentAgent?.id));
        if (!agentId) return;
        const { versionId, token } = yield select((s) => s.agentBuilder.generatedToken || {});
        const vid = payload?.versionId || versionId;
        const res = yield call(getAIAgentConfigDependencies, agentId, vid, token, {
          limit: 1000,
        });
        const data = res?.data || res;
        yield put({ type: 'saveDependencies', payload: data?.items || data || [] });
      } catch (e) {
        yield put({ type: 'saveDependencies', payload: [] });
      }
    },

    // ─────────────────────────────────────────────────────────────────────
    // Generic builder operations (append / modify / remove)
    //
    // These wrap the `ai/agent/config/{append|modify|remove}` endpoints with
    // the current token context. `elementType` must be one of
    // AIAgentBuilderElementType (configuration, greeting,
    // searching-human-agent-message, training-vector, guardrails, permissions,
    // openai-reasoning-effort, openai-reasoning-summary, openai-allowed-tools,
    // openai-vector-store-id).
    // ─────────────────────────────────────────────────────────────────────

    // Note: these effects intentionally do NOT swallow errors and do NOT
    // refresh the version snapshot per call. Callers (ConfigurationPanel)
    // batch multiple operations and decide at the end:
    //   - if all succeed → dispatch a single `fetchAgentConfig`
    //   - if any fails → surface a single error message and keep the
    //     drawer open with the unsaved state
    // This avoids N redundant refetches per Save and prevents "silent success"
    // when the global error handler shows a toast but the drawer closes anyway.
    *appendElement({ payload }, { call, select }) {
      const { elementType, data } = payload || {};
      const { aiAgentId, versionId, token, mode } = yield* getTokenContext(select);
      if (!aiAgentId || !versionId || !token || !isWriteMode(mode)) {
        throw new Error('AGENT_BUILDER_NO_WRITE_TOKEN');
      }
      const res = yield call(appendAIAgentConfigElement, aiAgentId, versionId, token, {
        elementType,
        data,
      });
      return res?.data || res;
    },

    *modifyElement({ payload }, { call, select }) {
      const { elementType, itemId, itemKey, data } = payload || {};
      const { aiAgentId, versionId, token, mode } = yield* getTokenContext(select);
      if (!aiAgentId || !versionId || !token || !isWriteMode(mode)) {
        throw new Error('AGENT_BUILDER_NO_WRITE_TOKEN');
      }
      const body = { elementType, data };
      if (itemId !== undefined) body.itemId = itemId;
      if (itemKey !== undefined) body.itemKey = itemKey;
      const res = yield call(modifyAIAgentConfigElement, aiAgentId, versionId, token, body);
      return res?.data || res;
    },

    *removeElement({ payload }, { call, select }) {
      const { elementType, itemId, itemKey, force } = payload || {};
      const { aiAgentId, versionId, token, mode } = yield* getTokenContext(select);
      if (!aiAgentId || !versionId || !token || !isWriteMode(mode)) {
        throw new Error('AGENT_BUILDER_NO_WRITE_TOKEN');
      }
      const body = { elementType };
      if (itemId !== undefined) body.itemId = itemId;
      if (itemKey !== undefined) body.itemKey = itemKey;
      if (force !== undefined) body.force = force;
      const res = yield call(removeAIAgentConfigElement, aiAgentId, versionId, token, body);
      return res?.data || res;
    },

    // ─────────────────────────────────────────────────────────────────────
    // Publish flow
    //
    // After a successful publish, backend creates a fresh draft. We must
    // request a new write token (retrying briefly because the new draft can
    // take a beat to materialise) and refresh both versions and config.
    // ─────────────────────────────────────────────────────────────────────

    *publishAgent({ payload } = {}, { call, put, select }) {
      const force = !!(payload && payload.force);
      const { aiAgentId, versionId, token, mode } = yield* getTokenContext(select);
      if (!aiAgentId || !versionId || !token) return;
      if (!isWriteMode(mode)) return;

      const translate = getIntl().formatMessage;
      yield put({ type: 'setPublishing', payload: true });
      try {
        const raw = yield call(publishAIAgentVersion, aiAgentId, versionId, token, force);
        // 200 OK body is an `AIAgentPublishResult` even when it reports
        // warnings; HTTP 4xx (config.Validate / invalid token) throws and is
        // handled in the catch below as a hard error (no force option).
        const res = raw?.data || raw;

        const inconsistencies = Array.isArray(res?.inconsistencies) ? res.inconsistencies : [];
        const published = !!res?.published;

        // Checkpoint: the backend generated the summary but found warnings and
        // did NOT publish. This is not a fatal error — surface the warnings so
        // the user can cancel or retry with force=true. Nothing changed
        // server-side, so we keep the current token / version untouched.
        if (!published) {
          if (inconsistencies.length > 0) {
            yield put({
              type: 'setPublishInconsistencies',
              payload: { inconsistencies, result: res },
            });
            return;
          }
          // published=false with no inconsistencies → the summary could not be
          // generated at all (LLM/JSON failure). Force can't help here.
          message.error(translate({ id: 'agentBuilder.publish.error' }));
          return;
        }

        // Published — clean or forced. Once the user chooses to force-publish
        // it's an intentional decision, so we don't nag with a persistent
        // warning afterwards; a plain success notification is enough.
        yield put({ type: 'clearPublishInconsistencies' });
        message.success(res?.message || translate({ id: 'agentBuilder.publish.success' }));

        // Refresh metadata too — publish mirrors name/email/modelId/memoryId
        // onto the base AIAgent on the backend.
        yield put({ type: 'refreshAgent' });

        // The publish result returns the renewed write token directly; honour
        // it (or the legacy `newToken`) before falling back to a generateToken
        // call.
        let writeToken = res?.token || res?.newToken || null;

        if (!writeToken) {
          try {
            writeToken = yield call(generateAIAgentConfigToken, aiAgentId, AGENT_ACCESS_MODE.Write);
            writeToken = writeToken?.data || writeToken;
          } catch (_) {
            writeToken = null;
          }
        }

        if (!writeToken || writeToken.versionId === versionId || !isWriteMode(writeToken.mode)) {
          yield call(() => new Promise((r) => setTimeout(r, 1500)));
          try {
            const retry = yield call(
              generateAIAgentConfigToken,
              aiAgentId,
              AGENT_ACCESS_MODE.Write,
            );
            writeToken = retry?.data || retry;
          } catch (_) {
            // Keep the previous writeToken (may still be valid).
          }
        }

        if (writeToken?.token) {
          writeCachedToken(writeToken);
          yield put({ type: 'saveToken', payload: writeToken });
          yield put({
            type: 'fetchAgentConfig',
            payload: {
              aiAgentId: writeToken.aiAgentId,
              versionId: writeToken.versionId,
              token: writeToken.token,
            },
          });
          yield put({
            type: 'fetchDependencies',
            payload: { aiAgentId, versionId: writeToken.versionId },
          });
        }

        // Refresh the version selector LAST, once we know the new draft's id.
        // The backend can take a beat to materialise the fresh draft, so we
        // retry once before falling back to a synthetic draft entry (mirrors
        // the app-builder publish flow). This guarantees the dropdown shows the
        // just-published version (now current/published) plus the new draft.
        const expectedVersionId = writeToken?.versionId;
        let items = [];
        try {
          const vr = yield call(getAIAgentVersions, aiAgentId);
          items = vr?.items || vr?.data?.items || [];
          if (expectedVersionId && !items.find((v) => v.id === expectedVersionId)) {
            yield call(() => new Promise((r) => setTimeout(r, 1500)));
            const retryVr = yield call(getAIAgentVersions, aiAgentId);
            items = retryVr?.items || retryVr?.data?.items || [];
          }
        } catch (_) {
          // Keep whatever we managed to fetch.
        }

        if (expectedVersionId && !items.find((v) => v.id === expectedVersionId)) {
          items = [
            { id: expectedVersionId, number: writeToken?.versionNumber, published: false },
            ...items,
          ];
        }
        yield put({ type: 'saveVersions', payload: items });
      } catch (e) {
        // Hard error (HTTP 4xx): structural config validation failed or the
        // token/version is no longer editable. These are NOT bypassable with
        // force — the user must fix the configuration.
        yield put({ type: 'clearPublishInconsistencies' });
        const errorMsg = e?.message || e?.data?.message || e?.Message;
        if (errorMsg) message.error(errorMsg);
      } finally {
        yield put({ type: 'setPublishing', payload: false });
      }
    },

    // ─────────────────────────────────────────────────────────────────────
    // Legacy metadata flow (PUT /ai/agent)
    //
    // The current UI still saves the entire agent (metadata + mirror fields)
    // through a single PUT. We keep this for backwards compatibility while
    // the new versioned drawers are wired up. The publish flow on the
    // backend keeps the AIAgent mirror fields in sync regardless.
    // ─────────────────────────────────────────────────────────────────────

    *saveAgentChanges({ payload, silent }, { call, put, select }) {
      const current = yield select((s) => s.agentBuilder.currentAgent);
      if (!current?.id) return;

      // PUT /ai/agent now only owns base entity metadata. Versioned runtime
      // fields (provider, instructions, configurations, connections, skills,
      // trainingVectors, guardrails, etc.) live on AIAgentVersion and are
      // updated through append/modify/remove + publish. Sending them here
      // would trigger a 400 Bad Request from the OData binder.
      const required = {
        name: current.name,
        ...(current.email !== undefined && { email: current.email }),
        ...(current.modelId !== undefined && { modelId: current.modelId }),
        ...(current.memoryId !== undefined && { memoryId: current.memoryId }),
        ...(current.aiAgentTeamId !== undefined && {
          aiAgentTeamId: current.aiAgentTeamId,
        }),
      };

      const merged = { ...required, ...payload, id: current.id };

      // Strip any legacy payload fields that callers might still pass through
      // mapAgentToFormValues / buildAgentPayload composition. They belong to
      // the versioned-config path now.
      [
        'provider',
        'configurations',
        'instructions',
        'connections',
        'skills',
        'vectors',
        'triggers',
        'trainingVectors',
        'publisher',
        'publishedDate',
        'accessControl',
        'guardrails',
        'greetings',
        'searchingHumanAgentMessages',
        'reasoningEffort',
        'reasoningSummary',
        'allowedTools',
        'vectorStoreId',
        'processes',
        'lists',
        'entities',
        'aiAgents',
      ].forEach((k) => delete merged[k]);

      // Defensive sweep for the flattened versioned blocks the wizard
      // exposes (the form spreads them into 6+ keys). Anything starting
      // with these prefixes belongs to the versioned config, never to
      // the base AIAgent entity.
      Object.keys(merged).forEach((k) => {
        if (typeof k !== 'string') return;
        if (k.startsWith('guardrails')) delete merged[k];
        if (k.startsWith('accessControl')) delete merged[k];
      });

      if (merged.icon && typeof merged.icon !== 'object') {
        merged.iconUrl = merged.icon;
        delete merged.icon;
      }

      yield put({ type: 'setSaving', payload: true });
      try {
        yield call(updateAIAgent, merged);

        // Re-fetch with the OData edit query so expanded fields
        // (connections, skills, trainingVectors) keep their full
        // {id, name} shape — the plain PUT response only returns ids.
        const refreshed = yield call(getAIAgentById, current.id, CoreQueries.ManageAgents.edit);
        const fresh = refreshed?.data || refreshed;

        if (fresh && fresh.id) {
          yield put({ type: 'saveAgent', payload: fresh });
        } else {
          yield put({ type: 'saveAgent', payload: { ...current, ...merged } });
        }
        yield put({ type: 'clearDraft' });
        yield put({ type: 'agents/clearAICache' });

        if (!silent) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }
      } catch (error) {
        const validationErrors = error?.validationErrors || [];
        const itemDeletedError = validationErrors.find(
          (ve) =>
            ve.errorCode === VALIDATION_INTERNAL_ERRORS.VALIDATION_ITEM_DELETED_EXISTS &&
            ve.metadata?.id,
        );
        if (itemDeletedError) {
          const translate = getIntl().formatMessage;
          const msg =
            (itemDeletedError.errorMessages && itemDeletedError.errorMessages[0]) ||
            error?.message ||
            translate({ id: 'agentBuilder.save.error' });
          message.error(msg);
          return;
        }

        if (error?.message) {
          message.error(error.message);
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({ type: 'setSaving', payload: false });
      }
    },

    *deleteAgent({ payload }, { call, put }) {
      const { id } = payload || {};
      if (!id) return;
      try {
        yield call(deleteAIAgent, id);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'agents/clearAICache' });
      } catch (error) {
        if (error?.message) {
          message.error(error.message);
        }
      }
    },

    *createAgent({ payload }, { call, put }) {
      const transformed = { ...payload };
      if (transformed.icon && typeof transformed.icon !== 'object') {
        transformed.iconUrl = transformed.icon;
        delete transformed.icon;
      }

      try {
        const response = yield call(createAIAgent, transformed);
        const saved = response?.data || response;
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'agents/clearAICache' });
        return saved;
      } catch (error) {
        // IMPORTANT: do NOT re-throw here. Re-throwing routes the error to
        // DVA's global onError, which swallows it and leaves the promise
        // returned by `dispatch` unsettled — the caller's `await dispatch`
        // would hang forever and never reset its submit button. The global
        // request error handler already surfaces a toast for the failure, so
        // we just normalise the payload and return it so the drawer can map
        // the field-level validation errors inline and re-enable the button.
        return {
          error: true,
          message: error?.message,
          validationErrors: error?.validationErrors || [],
        };
      }
    },

    // ─── Collaborators (AIAgent domain) ───────────────────────────────
    //
    // Backend contract (see "AccessControl vs Collaborator" UX guide):
    //   GET    /ai/agent/config/collaborators/{aiAgentId}
    //   POST   /ai/agent/config/collaborators/{aiAgentId}  body: { collaboratorId }
    //   DELETE /ai/agent/config/collaborators/{aiAgentId}/{collaboratorRecordId}
    //
    // Refresh contract: every mutation triggers a single inline GET
    // against the primary replica (`readPriority: true`) so the drawer
    // and the row-command kebab see the row we just POSTed/DELETEd
    // without waiting for the read replica to catch up.
    // ─────────────────────────────────────────────────────────────────

    *fetchAgentCollaborators({ payload }, { call, put, select }) {
      try {
        const aiAgentId =
          payload?.aiAgentId || (yield select((s) => s.agentBuilder.currentAgent?.id));
        if (!aiAgentId) {
          yield put({ type: 'saveAgentCollaborators', payload: [] });
          return;
        }
        const res = yield call(getAIAgentCollaborators, aiAgentId, {
          readPriority: !!payload?.readPriority,
        });
        const raw = res?.items || res?.data?.items || res?.data || res || [];
        const list = Array.isArray(raw) ? raw : [];
        // Single batched `user/paged` call keeps the GET contract
        // minimal (no $expand assumed) and works regardless of which
        // navigation property the backend uses for the join.
        const hydrated = yield call(hydrateCollaboratorUsers, list);
        yield put({ type: 'saveAgentCollaborators', payload: hydrated });
      } catch (e) {
        yield put({ type: 'saveAgentCollaborators', payload: [] });
      }
    },

    *addAgentCollaborator({ payload }, { call, put, select }) {
      const aiAgentId =
        payload?.aiAgentId || (yield select((s) => s.agentBuilder.currentAgent?.id));
      try {
        if (aiAgentId && payload?.collaboratorId) {
          yield call(addAIAgentCollaborator, aiAgentId, payload.collaboratorId);
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors,
          });
        }
      }
      if (aiAgentId) {
        yield put({
          type: 'fetchAgentCollaborators',
          payload: { aiAgentId, readPriority: true },
        });
      }
    },

    *removeAgentCollaborator({ payload }, { call, put, select }) {
      const aiAgentId =
        payload?.aiAgentId || (yield select((s) => s.agentBuilder.currentAgent?.id));
      try {
        if (aiAgentId && payload?.id) {
          yield call(removeAIAgentCollaborator, aiAgentId, payload.id);
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
      if (aiAgentId) {
        yield put({
          type: 'fetchAgentCollaborators',
          payload: { aiAgentId, readPriority: true },
        });
      }
    },
  },

  reducers: {
    saveAgent(state, { payload }) {
      return { ...state, currentAgent: payload, initError: null };
    },
    saveToken(state, { payload }) {
      return { ...state, generatedToken: payload };
    },
    saveVersions(state, { payload }) {
      return { ...state, versions: payload || [] };
    },
    saveConfigurations(state, { payload }) {
      return { ...state, configurations: payload };
    },
    saveDependencies(state, { payload }) {
      return { ...state, dependencies: payload || [] };
    },
    queryTraces(state, { payload }) {
      return {
        ...state,
        traces: payload.items || [],
        tracesPagination: {
          ...state.tracesPagination,
          ...payload,
          ...toPaginate(payload),
        },
      };
    },
    queryOrderBy(state, { payload }) {
      return { ...state, tracesOrderBy: payload || 'created desc' };
    },
    filtersConfig(state, { payload }) {
      return { ...state, filtersConfig: payload || [] };
    },
    setLoading(state, { payload }) {
      return { ...state, loading: !!payload };
    },
    setSaving(state, { payload }) {
      return { ...state, saving: !!payload };
    },
    setPublishing(state, { payload }) {
      return { ...state, publishing: !!payload };
    },
    setPublishInconsistencies(state, { payload }) {
      return { ...state, publishInconsistencies: payload || null };
    },
    clearPublishInconsistencies(state) {
      return { ...state, publishInconsistencies: null };
    },
    initFailed(state, { payload }) {
      return { ...state, initError: payload };
    },
    updateDraft(state, { payload }) {
      if (!payload || typeof payload !== 'object') return state;
      return { ...state, draft: { ...state.draft, ...payload } };
    },
    clearDraft(state) {
      return { ...state, draft: {} };
    },
    saveAgentCollaborators(state, { payload }) {
      return { ...state, collaborators: payload || [] };
    },
    resetBuilder() {
      clearCachedToken();
      return initialState;
    },
  },
};

export default Model;
